import { createContext, useContext, useState } from "react";

const LangContext = createContext({ lang: "en", setLang: () => {} });

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("en");
  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);

export const NAV_LABELS = {
  en: { product: "Product", how: "How It Works", pricing: "Pricing", contact: "Contact", login: "Login", start: "Start Setup" },
  hi: { product: "प्रोडक्ट", how: "कैसे काम करता है", pricing: "कीमत", contact: "संपर्क करें", login: "लॉगिन", start: "सेटअप शुरू करें" },
};

export const HOME = {
  en: {
    heroBadge: "AI Calling powered by TalkID",
    heroLines: ["Your Complete", "AI Employee", "for Business"],
    heroDesc: "One AI system to manage your customer calls, WhatsApp, emails, orders, follow-ups and social media — understanding every conversation like your best team member.",
    ctaStart: "Start Your AI Employee",
    ctaHow: "See How It Works",
    marquee: ["Answers customer calls", "Replies on WhatsApp", "Responds to emails", "Takes orders", "Sends tracking updates", "Follows up automatically", "Drafts social posts", "Works 24/7"],
    trust: [
      { t: "Works 24/7", d: "Never miss a customer, even at night" },
      { t: "Replies like your business", d: "In your tone, with your information" },
      { t: "All channels in one place", d: "Calls, WhatsApp, email and website" },
      { t: "Human takeover anytime", d: "Step into any conversation yourself" },
    ],
    featEyebrow: "One platform, every customer channel",
    featTitle: "Everything your business needs",
    featDesc: "Simple tools that quietly handle your daily customer work, so you can focus on running your business.",
    features: [
      { t: "AI Calling", d: "A friendly AI call agent answers customer calls, shares prices and takes requests — day and night.", badge: "Powered by TalkID" },
      { t: "WhatsApp Replies", d: "Instant, polite replies to customer messages on WhatsApp, in English or Hindi." },
      { t: "Email Support", d: "Customer emails get clear, helpful answers written the way your business would write them." },
      { t: "Orders and Follow-ups", d: "Takes orders, sends order and tracking updates, and reminds customers at the right time." },
      { t: "Social Media", d: "Creates posts and images for your approval, then publishes what you approve." },
      { t: "Website and Store Connection", d: "Learns from your website, documents and past conversations to answer correctly." },
    ],
    undEyebrow: "Samajh kar baat karta hai",
    undTitle: "Not a robot. It truly understands your customers.",
    undDesc: "No fixed menus, no \"press 1 for this\". Your AI understands real conversation — even half-sentences and Hinglish — and replies the way your best employee would.",
    undBullets: [
      { t: "Understands context, not just keywords", d: "\"Woh blue wala jo kal poocha tha\" — it knows exactly what the customer means." },
      { t: "Trained by our team, for your business only", d: "We train it on your products, prices, policies and past chats — A to Z, complete." },
      { t: "Speaks like your customer", d: "Hindi, English or Hinglish — it matches the customer's language and tone." },
      { t: "No coding needed. Ever.", d: "Our team sets up and trains everything. You just review and approve." },
    ],
    chatLabel: "A real-style conversation",
    chatNote: "It remembered yesterday's question, checked stock, took the order and sent payment — all by itself.",
    teamEyebrow: "Ek AI system, andar poori team",
    teamTitle: "The work of 10 people. One AI system.",
    teamDesc: "Inside your one simple dashboard, a team of specialist AI agents handles every kind of work — together, without breaks, without salary, without coding.",
    agents: [
      { name: "Riya", role: "Call Agent", d: "Answers every call, takes orders on phone" },
      { name: "Kabir", role: "WhatsApp Agent", d: "Instant replies, confirmations, payment links" },
      { name: "Meera", role: "Email Agent", d: "Clear, professional email answers" },
      { name: "Aarav", role: "Order Agent", d: "Cart links, website orders, courier tracking" },
      { name: "Zoya", role: "Social Agent", d: "Drafts posts and images for your approval" },
    ],
    teamStats: [
      { big: "10 people's work", small: "handled round the clock" },
      { big: "4–5 specialist agents", small: "inside one simple system" },
      { big: "Zero coding", small: "we set up everything for you" },
    ],
    howEyebrow: "Simple setup, no technical knowledge needed",
    howTitle: "How it works",
    howSteps: [
      { t: "Tell us about your business", d: "Share your website, products, prices and common customer questions. Our team trains your AI on everything — A to Z." },
      { t: "Connect your business channels", d: "Link your phone, WhatsApp, email and social accounts with simple connect buttons. No passwords shared with us." },
      { t: "Test and start your AI employee", d: "Try a test call and chat first. When you are happy, switch it on with one button." },
    ],
    wfEyebrow: "Real journeys, start to finish",
    wfTitle: "See exactly what your AI employee does",
    wfDesc: "Every business gets its own workflow — set up completely by our team, without any human needed in daily work.",
    wfPlay: "Play demo",
    wfPause: "Pause demo",
    wfNote: "Clinic, shop, service business, coaching — har business ka apna workflow. Our team builds and trains it completely for your business.",
    flows: {
      order: {
        label: "Product Order",
        steps: [
          { t: "Customer reaches out", d: "Call, WhatsApp, email or website — a product question, order status, or anything about your business." },
          { t: "AI understands and replies", d: "Price, stock, delivery time — instant, correct answers in the customer's own language." },
          { t: "Order is taken", d: "Product and quantity are confirmed to the customer on both WhatsApp and email." },
          { t: "Order agent takes over", d: "Sends a ready-to-pay cart link with the product already added — or collects payment on WhatsApp and places the order on your website automatically." },
          { t: "Live order status", d: "AI checks the courier website itself and tells the customer exactly where the parcel is." },
          { t: "After delivery", d: "AI thanks the customer, politely asks for a review, and notes the follow-up for you." },
        ],
      },
      appointment: {
        label: "Doctor Appointment",
        steps: [
          { t: "Patient reaches out", d: "Call, WhatsApp or email to the clinic — the AI answers instantly, any time of day." },
          { t: "AI understands the need", d: "Shares the right doctor, available timings and fees — no waiting on hold." },
          { t: "Appointment booked", d: "Confirmation sent on both WhatsApp and email with date, time and instructions." },
          { t: "Automatic reminders", d: "A reminder before the visit, and easy rescheduling if plans change." },
          { t: "After the visit", d: "Follow-up message, help with reports, and a polite review request." },
        ],
      },
      salon: {
        label: "Salon / Service",
        steps: [
          { t: "Customer reaches out", d: "Call, WhatsApp or website — a haircut, a repair visit, or any service enquiry." },
          { t: "AI understands and suggests", d: "Shares your services, prices and the next available time slots instantly." },
          { t: "Booking confirmed", d: "Time slot confirmed on both WhatsApp and email with address and instructions." },
          { t: "Reminder before the visit", d: "Automatic reminder a few hours before, with easy rescheduling." },
          { t: "After the service", d: "Thank-you message, review request and a smart suggestion for the next visit." },
        ],
      },
    },
    ctrlEyebrow: "Safety first",
    ctrlTitle: "You stay in control",
    ctrlDesc: "The AI employee works for you — never the other way around. Decide what it can do, and step in whenever you like.",
    ctrlItems: [
      { t: "Approve important actions", d: "Refunds, discounts and special replies wait for your approval." },
      { t: "Take over any conversation", d: "One tap and you are talking to the customer yourself." },
      { t: "Set what AI can and cannot do", d: "Simple switches for every kind of task." },
      { t: "See complete activity history", d: "Every call, message and order is recorded for you." },
    ],
    apprLabel: "Needs your approval",
    apprTitle: "Refund request — ₹2,450",
    apprWaiting: "Waiting",
    apprDesc: "Customer Priya asked for a refund on order #1042 (mixer grinder, damaged in delivery). AI has prepared a polite confirmation message.",
    apprApprove: "Approve",
    apprReview: "Review first",
    apprNote: "Example from the dashboard. Nothing this important happens without you.",
    priceEyebrow: "Simple plans",
    priceTitle: "Pricing that fits your business",
    priceDesc: "Start small, grow when you are ready. Cancel anytime.",
    plans: [
      { name: "Starter", d: "For small businesses getting started" },
      { name: "Business", d: "For businesses handling regular customer conversations" },
      { name: "Custom", d: "For businesses needing custom automation and integrations" },
    ],
    popular: "Most Popular",
    viewPlan: "View plan details",
    faqEyebrow: "Common questions",
    faqTitle: "Frequently asked questions",
    faqs: [
      { q: "Will it talk like a robot?", a: "No. It understands the full conversation — even half-sentences and Hinglish — and replies naturally in your business's tone. Our team trains it specially on your products, prices, policies and past chats, A to Z." },
      { q: "Do I need any coding or technical knowledge?", a: "Not at all. Our team sets up, trains and tests everything for you. You only review and approve from a simple dashboard." },
      { q: "Can the AI really take complete orders by itself?", a: "Yes. It confirms the product and quantity on WhatsApp and email, sends a ready-to-pay cart link — or collects payment on WhatsApp and places the order on your website automatically." },
      { q: "Can it reply on WhatsApp?", a: "Yes. It connects to your WhatsApp Business number and replies instantly, in English or Hindi, using your business information." },
      { q: "Can a human take over a conversation?", a: "Anytime. You or your team can step into any conversation with one tap, and the AI politely steps back." },
      { q: "Is my business data kept separately?", a: "Yes. Your business information, customer conversations and documents are kept separate and are never shared with other businesses." },
      { q: "Can I cancel my plan?", a: "Yes, you can cancel anytime from your settings. No lock-in, no questions asked. See our Refund and Cancellation Policy for details." },
    ],
    ctaTitle: "Set up your AI employee for your business",
    ctaDesc: "Ready in a few simple steps. Trained completely by our team — test everything before it talks to a single customer.",
    ctaDemo: "Book a Demo",
  },
  hi: {
    heroBadge: "AI कॉलिंग — TalkID द्वारा संचालित",
    heroLines: ["आपका पूरा", "AI एम्प्लॉई", "आपके बिज़नेस के लिए"],
    heroDesc: "एक AI सिस्टम जो आपके customer calls, WhatsApp, emails, orders, follow-ups और social media संभाले — हर बातचीत को आपके सबसे अच्छे टीम मेंबर की तरह समझते हुए।",
    ctaStart: "अपना AI एम्प्लॉई शुरू करें",
    ctaHow: "देखें कैसे काम करता है",
    marquee: ["Customer calls का जवाब", "WhatsApp पर तुरंत जवाब", "Emails का जवाब", "Orders लेता है", "Tracking updates भेजता है", "Follow-up अपने आप", "Social posts बनाता है", "24/7 काम करता है"],
    trust: [
      { t: "24/7 काम करता है", d: "रात में भी कोई customer miss नहीं" },
      { t: "आपके business जैसा जवाब", d: "आपके अंदाज़ में, आपकी जानकारी के साथ" },
      { t: "सभी channels एक जगह", d: "Calls, WhatsApp, email और website" },
      { t: "कभी भी खुद संभालें", d: "किसी भी बातचीत में खुद आ जाएँ" },
    ],
    featEyebrow: "एक platform, हर customer channel",
    featTitle: "आपके business की हर ज़रूरत",
    featDesc: "सरल tools जो आपके रोज़ के customer काम चुपचाप संभालते हैं, ताकि आप business चलाने पर ध्यान दें।",
    features: [
      { t: "AI कॉलिंग", d: "एक friendly AI call agent customer calls का जवाब देता है, prices बताता है और requests लेता है — दिन हो या रात।", badge: "TalkID द्वारा संचालित" },
      { t: "WhatsApp जवाब", d: "WhatsApp पर customer messages के तुरंत, विनम्र जवाब — English या Hindi में।" },
      { t: "Email सहायता", d: "Customer emails के साफ़, मददगार जवाब — बिल्कुल आपके business की तरह।" },
      { t: "Orders और Follow-ups", d: "Orders लेता है, order और tracking updates भेजता है, और सही समय पर customers को याद दिलाता है।" },
      { t: "Social Media", d: "आपकी approval के लिए posts और images बनाता है, फिर approved posts publish करता है।" },
      { t: "Website और Store कनेक्शन", d: "आपकी website, documents और पुरानी बातचीत से सीखकर सही जवाब देता है।" },
    ],
    undEyebrow: "समझकर बात करता है",
    undTitle: "Robot नहीं। ये सच में समझता है।",
    undDesc: "कोई fixed menu नहीं, कोई 'press 1' नहीं। आपका AI असली बातचीत समझता है — अधूरे वाक्य और Hinglish भी — और आपके सबसे अच्छे employee की तरह जवाब देता है।",
    undBullets: [
      { t: "सिर्फ keywords नहीं, context समझता है", d: "'Woh blue wala jo kal poocha tha' — इसे पता है customer क्या कह रहा है।" },
      { t: "हमारी team सिर्फ आपके business के लिए train करती है", d: "आपके products, prices, policies और पुरानी chats पर — A to Z, पूरा।" },
      { t: "Customer की भाषा में बात करता है", d: "Hindi, English या Hinglish — customer की भाषा और अंदाज़ से मेल खाता है।" },
      { t: "Coding की कोई ज़रूरत नहीं। कभी नहीं।", d: "हमारी team सब कुछ set up और train करती है। आप बस review और approve करें।" },
    ],
    chatLabel: "एक असली जैसी बातचीत",
    chatNote: "इसने कल का सवाल याद रखा, stock check किया, order लिया और payment भेजा — सब खुद।",
    teamEyebrow: "एक AI system, अंदर पूरी team",
    teamTitle: "10 लोगों का काम। एक AI system।",
    teamDesc: "आपके एक simple dashboard के अंदर, specialist AI agents की पूरी team हर तरह का काम संभालती है — बिना break, बिना salary, बिना coding।",
    agents: [
      { name: "Riya", role: "Call Agent", d: "हर call का जवाब, phone पर orders" },
      { name: "Kabir", role: "WhatsApp Agent", d: "तुरंत जवाब, confirmations, payment links" },
      { name: "Meera", role: "Email Agent", d: "साफ़, professional email जवाब" },
      { name: "Aarav", role: "Order Agent", d: "Cart links, website orders, courier tracking" },
      { name: "Zoya", role: "Social Agent", d: "आपकी approval के लिए posts और images" },
    ],
    teamStats: [
      { big: "10 लोगों का काम", small: "दिन-रात संभाला जाता है" },
      { big: "4–5 specialist agents", small: "एक simple system के अंदर" },
      { big: "Zero coding", small: "हम सब कुछ set up करते हैं" },
    ],
    howEyebrow: "Simple setup, technical knowledge की ज़रूरत नहीं",
    howTitle: "कैसे काम करता है",
    howSteps: [
      { t: "अपने business के बारे में बताएं", d: "अपनी website, products, prices और आम सवाल share करें। हमारी team आपके AI को सब कुछ train करती है — A to Z।" },
      { t: "अपने business channels जोड़ें", d: "Simple connect buttons से phone, WhatsApp, email और social accounts जोड़ें। कोई password हमारे साथ share नहीं।" },
      { t: "Test करें और शुरू करें", d: "पहले एक test call और chat आज़माएँ। जब खुश हों, एक button से चालू करें।" },
    ],
    wfEyebrow: "असली journeys, शुरू से अंत तक",
    wfTitle: "देखिए आपका AI employee क्या-क्या करता है",
    wfDesc: "हर business को अपना workflow मिलता है — हमारी team द्वारा पूरा set up, रोज़ के काम में किसी इंसान की ज़रूरत के बिना।",
    wfPlay: "डेमो चलाएँ",
    wfPause: "डेमो रोकें",
    wfNote: "Clinic, shop, service business, coaching — हर business का अपना workflow। हमारी team इसे आपके business के लिए पूरी तरह बनाती और train करती है।",
    flows: {
      order: {
        label: "प्रोडक्ट ऑर्डर",
        steps: [
          { t: "Customer संपर्क करता है", d: "Call, WhatsApp, email या website — product का सवाल, order status, या business से जुड़ा कोई भी सवाल।" },
          { t: "AI समझकर जवाब देता है", d: "Price, stock, delivery time — customer की अपनी भाषा में तुरंत सही जवाब।" },
          { t: "Order ले लिया जाता है", d: "Product और quantity की confirmation customer को WhatsApp और email दोनों पर।" },
          { t: "Order agent आगे संभालता है", d: "Ready-to-pay cart link भेजता है — या WhatsApp पर payment लेकर आपकी website पर order खुद place कर देता है।" },
          { t: "Live order status", d: "AI खुद courier website check करके बताता है कि parcel कहाँ है।" },
          { t: "Delivery के बाद", d: "AI customer को धन्यवाद कहता है, विनम्रता से review पूछता है, और follow-up note करता है।" },
        ],
      },
      appointment: {
        label: "डॉक्टर अपॉइंटमेंट",
        steps: [
          { t: "Patient संपर्क करता है", d: "Clinic को call, WhatsApp या email — AI दिन के किसी भी समय तुरंत जवाब देता है।" },
          { t: "AI ज़रूरत समझता है", d: "सही doctor, available timings और fees बताता है — hold पर इंतज़ार नहीं।" },
          { t: "Appointment book हो गई", d: "WhatsApp और email दोनों पर date, time और instructions के साथ confirmation।" },
          { t: "अपने आप reminders", d: "Visit से पहले reminder, और plan बदलने पर आसान rescheduling।" },
          { t: "Visit के बाद", d: "Follow-up message, reports में मदद, और विनम्र review request।" },
        ],
      },
      salon: {
        label: "सैलून / सर्विस",
        steps: [
          { t: "Customer संपर्क करता है", d: "Call, WhatsApp या website — haircut, repair visit, या कोई भी service की enquiry।" },
          { t: "AI समझकर suggest करता है", d: "आपकी services, prices और अगले available time slots तुरंत बताता है।" },
          { t: "Booking confirm", d: "WhatsApp और email दोनों पर time slot की confirmation, address और instructions के साथ।" },
          { t: "Visit से पहले reminder", d: "कुछ घंटे पहले अपने आप reminder, और आसान rescheduling।" },
          { t: "Service के बाद", d: "Thank-you message, review request और अगली visit का smart suggestion।" },
        ],
      },
    },
    ctrlEyebrow: "सुरक्षा पहले",
    ctrlTitle: "Control आपके हाथ में",
    ctrlDesc: "AI employee आपके लिए काम करता है — उल्टा कभी नहीं। तय करें वह क्या कर सकता है, और जब चाहें बीच में आएँ।",
    ctrlItems: [
      { t: "ज़रूरी actions को approve करें", d: "Refunds, discounts और खास जवाब आपकी approval का इंतज़ार करते हैं।" },
      { t: "किसी भी बातचीत को खुद संभालें", d: "एक tap और आप खुद customer से बात कर रहे हैं।" },
      { t: "तय करें AI क्या कर सकता है और क्या नहीं", d: "हर काम के लिए simple switches।" },
      { t: "पूरी activity history देखें", d: "हर call, message और order आपके लिए दर्ज है।" },
    ],
    apprLabel: "आपकी approval चाहिए",
    apprTitle: "Refund request — ₹2,450",
    apprWaiting: "इंतज़ार में",
    apprDesc: "Customer Priya ने order #1042 (mixer grinder, delivery में टूटा) का refund माँगा। AI ने विनम्र confirmation तैयार किया है।",
    apprApprove: "मंज़ूर करें",
    apprReview: "पहले देखें",
    apprNote: "Dashboard से उदाहरण। इतना ज़रूरी काम आपके बिना कुछ नहीं होता।",
    priceEyebrow: "आसान plans",
    priceTitle: "आपके business के हिसाब से कीमत",
    priceDesc: "छोटी शुरुआत करें, ready होने पर बढ़ाएँ। कभी भी cancel करें।",
    plans: [
      { name: "Starter", d: "शुरुआत करने वाले छोटे businesses के लिए" },
      { name: "Business", d: "नियमित customer बातचीत संभालने वाले businesses के लिए" },
      { name: "Custom", d: "Custom automation और integrations चाहने वाले businesses के लिए" },
    ],
    popular: "सबसे लोकप्रिय",
    viewPlan: "पूरा plan देखें",
    faqEyebrow: "आम सवाल",
    faqTitle: "अक्सर पूछे जाने वाले सवाल",
    faqs: [
      { q: "क्या ये robot की तरह बात करेगा?", a: "नहीं। यह पूरी बातचीत समझता है — अधूरे वाक्य और Hinglish भी — और आपके business के अंदाज़ में naturally जवाब देता है। हमारी team इसे आपके products, prices, policies और पुरानी chats पर A to Z train करती है।" },
      { q: "क्या मुझे coding आनी चाहिए?", a: "बिल्कुल नहीं। हमारी team सब कुछ set up, train और test करती है। आप बस simple dashboard से review और approve करें।" },
      { q: "क्या AI सच में पूरा order खुद ले सकता है?", a: "हाँ। यह product और quantity को WhatsApp और email पर confirm करता है, ready-to-pay cart link भेजता है — या WhatsApp पर payment लेकर आपकी website पर order खुद place कर देता है।" },
      { q: "क्या यह WhatsApp पर जवाब दे सकता है?", a: "हाँ। यह आपके WhatsApp Business number से जुड़ता है और English या Hindi में तुरंत जवाब देता है।" },
      { q: "क्या कोई इंसान बातचीत संभाल सकता है?", a: "कभी भी। आप या आपकी team एक tap से किसी भी बातचीत में आ सकते हैं, और AI विनम्रता से पीछे हो जाता है।" },
      { q: "क्या मेरा business data अलग रखा जाता है?", a: "हाँ। आपकी business जानकारी, customer बातचीत और documents अलग रखे जाते हैं और कभी दूसरे businesses के साथ share नहीं होते।" },
      { q: "क्या मैं plan cancel कर सकता/सकती हूँ?", a: "हाँ, settings से कभी भी cancel करें। कोई lock-in नहीं। Refund and Cancellation Policy देखें।" },
    ],
    ctaTitle: "अपने business के लिए AI employee set up करें",
    ctaDesc: "कुछ आसान steps में तैयार। हमारी team द्वारा पूरी तरह trained — किसी भी customer से बात करने से पहले सब कुछ test करें।",
    ctaDemo: "Demo बुक करें",
  },
};
